import { useState } from "react";
import Api from "../../../shared/api/api";

const QUESTION_COUNT = 20;

const createEmptyQuestion = (id) => ({
  id,
  question: "",
  choiceA: "",
  choiceB: "",
  choiceC: "",
  choiceD: "",
  answer: "",
  questionType:
    id <= 10 ? "Knowledge" : "Identification",
  image: "",
  level: "",
});

const createInitialQuestions = () =>
  Array.from(
    { length: QUESTION_COUNT },
    (_, index) => createEmptyQuestion(index + 1)
  );

export default function AddTestForm({
  onTestAdded,
}) {
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState(
    createInitialQuestions
  );

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [status, setStatus] = useState("");
  const [isError, setIsError] = useState(false);

  const handleQuestionChange = (
    index,
    field,
    value
  ) => {
    setQuestions((currentQuestions) =>
      currentQuestions.map(
        (question, questionIndex) =>
          questionIndex === index
            ? {
                ...question,
                [field]: value,
              }
            : question
      )
    );

    setStatus("");
    setIsError(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedTitle = title.trim();

    if (!normalizedTitle) {
      setIsError(true);
      setStatus("A test title is required.");
      return;
    }

    const normalizedQuestions = questions.map(
      (question) => ({
        ...question,
        question: question.question.trim(),
        choiceA: question.choiceA.trim(),
        choiceB: question.choiceB.trim(),
        choiceC: question.choiceC.trim(),
        choiceD: question.choiceD.trim(),
        answer: question.answer.trim(),
        image: question.image.trim(),
        level: question.level.trim(),
      })
    );

    setIsSubmitting(true);
    setStatus("");
    setIsError(false);

    try {
      const response = await Api.addTest({
        title: normalizedTitle,
        questions: normalizedQuestions,
      });

      if (response?.error) {
        throw new Error(response.error);
      }

      setTitle("");
      setQuestions(createInitialQuestions());
      setStatus("The test was added successfully.");

      if (typeof onTestAdded === "function") {
        onTestAdded(response);
      }
    } catch (error) {
      console.error(
        "Unable to add FourH test:",
        error
      );

      setIsError(true);
      setStatus(
        error?.message ||
          "The test could not be added."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="addQuestionsForm"
      onSubmit={handleSubmit}
    >
      <div className="addTestTitleField">
        <label htmlFor="new-test-title">
          Test Title
        </label>

        <input
          id="new-test-title"
          type="text"
          value={title}
          onChange={(event) =>
            setTitle(event.target.value)
          }
          placeholder="Test title"
          required
        />
      </div>

      <div className="addQuestionsDiv fourHTableWrapper">
        <table className="adminTable">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Question</th>
              <th scope="col">Choice A</th>
              <th scope="col">Choice B</th>
              <th scope="col">Choice C</th>
              <th scope="col">Choice D</th>
              <th scope="col">Answer</th>
              <th scope="col">
                Question Type
              </th>
              <th scope="col">Level</th>
              <th scope="col">Image</th>
            </tr>
          </thead>

          <tbody>
            {questions.map((question, index) => (
              <tr key={`NewQuestion-${question.id}`}>
                <td>
                  <span className="questionIdP">
                    {question.id}
                  </span>
                </td>

                <td>
                  <textarea
                    className="addQuestionTextArea"
                    value={question.question}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "question",
                        event.target.value
                      )
                    }
                    placeholder="Question"
                    aria-label={`Question ${question.id}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.choiceA}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "choiceA",
                        event.target.value
                      )
                    }
                    placeholder="Choice A"
                    aria-label={`Question ${question.id} choice A`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.choiceB}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "choiceB",
                        event.target.value
                      )
                    }
                    placeholder="Choice B"
                    aria-label={`Question ${question.id} choice B`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.choiceC}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "choiceC",
                        event.target.value
                      )
                    }
                    placeholder="Choice C"
                    aria-label={`Question ${question.id} choice C`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.choiceD}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "choiceD",
                        event.target.value
                      )
                    }
                    placeholder="Choice D"
                    aria-label={`Question ${question.id} choice D`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.answer}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "answer",
                        event.target.value
                      )
                    }
                    placeholder="Answer"
                    aria-label={`Question ${question.id} answer`}
                  />
                </td>

                <td>
                  <select
                    className="usersSelect"
                    value={question.questionType}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "questionType",
                        event.target.value
                      )
                    }
                    aria-label={`Question ${question.id} type`}
                  >
                    <option value="Knowledge">
                      Knowledge
                    </option>
                    <option value="Identification">
                      Identification
                    </option>
                  </select>
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.level}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "level",
                        event.target.value
                      )
                    }
                    placeholder="Level"
                    aria-label={`Question ${question.id} level`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="userUpdateInputs"
                    value={question.image}
                    onChange={(event) =>
                      handleQuestionChange(
                        index,
                        "image",
                        event.target.value
                      )
                    }
                    placeholder="Image"
                    aria-label={`Question ${question.id} image`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        className="addBtn"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting
          ? "Adding Test..."
          : "Add New Test"}
      </button>

      {status && (
        <p
          className={`fourHFormStatus${
            isError ? " error" : " success"
          }`}
          role={isError ? "alert" : "status"}
        >
          {status}
        </p>
      )}
    </form>
  );
}